import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core';
import {
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  finalize,
  map,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { CentroLogisticoCementoApiService } from '../services/centro-logistico-cemento-api.service';
import { CentroLogisticoCementoUpsertCommand } from '../models/centro-logistico-cemento.model';
import { CentroLogisticoCementoVm } from '../models/centro-logistico-cemento.vm';
import { CentroLogisticoCementoMapper } from '../pages/centros-logisticos/mappers/centro-logistico-cemento.mapper';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_ORDERING = 'id';

@Injectable()
export class CentrosLogisticosCementoStore implements OnDestroy {
  private readonly api = inject(CentroLogisticoCementoApiService);
  private readonly loadingDepth = signal(0);
  private readonly searchChanges$ = new Subject<string>();
  private readonly searchSubscription: Subscription;

  readonly items = signal<CentroLogisticoCementoVm[]>([]);
  readonly totalItems = signal(0);
  readonly page = signal(DEFAULT_PAGE);
  readonly pageSize = signal(DEFAULT_PAGE_SIZE);
  readonly searchTerm = signal('');
  readonly ordering = signal(DEFAULT_ORDERING);
  readonly error = signal<string | null>(null);
  readonly loading = computed(() => this.loadingDepth() > 0);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.pageSize())));
  readonly hasPreviousPage = computed(() => this.page() > 1);
  readonly hasNextPage = computed(() => this.page() < this.totalPages());
  readonly pageStartIndex = computed(() => {
    if (this.totalItems() === 0) {
      return 0;
    }
    return (this.page() - 1) * this.pageSize() + 1;
  });
  readonly pageEndIndex = computed(() => Math.min(this.totalItems(), this.page() * this.pageSize()));

  constructor() {
    this.searchSubscription = this.searchChanges$
      .pipe(
        debounceTime(350),
        distinctUntilChanged(),
        switchMap(() => this.loadItemsInternal({ propagateError: false })),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  loadItems(): Observable<void> {
    return this.loadItemsInternal({ propagateError: true });
  }

  setSearchTerm(value: string): void {
    const normalized = String(value ?? '');
    if (this.searchTerm() === normalized) {
      return;
    }
    this.searchTerm.set(normalized);
    this.page.set(DEFAULT_PAGE);
    this.searchChanges$.next(normalized);
  }

  setOrdering(value: string): void {
    const normalized = value.trim() || DEFAULT_ORDERING;
    if (this.ordering() === normalized) {
      return;
    }
    this.ordering.set(normalized);
    this.page.set(DEFAULT_PAGE);
    this.loadItemsInternal({ propagateError: false }).subscribe();
  }

  setPage(value: number): void {
    const pageValue = Number.isFinite(value) ? Math.max(DEFAULT_PAGE, Math.trunc(value)) : DEFAULT_PAGE;
    if (this.page() === pageValue) {
      return;
    }
    this.page.set(pageValue);
    this.loadItemsInternal({ propagateError: false }).subscribe();
  }

  setPageSize(value: number): void {
    const sizeValue = Number.isFinite(value) ? Math.max(1, Math.trunc(value)) : DEFAULT_PAGE_SIZE;
    if (this.pageSize() === sizeValue) {
      return;
    }
    this.pageSize.set(sizeValue);
    this.page.set(DEFAULT_PAGE);
    this.loadItemsInternal({ propagateError: false }).subscribe();
  }

  clearError(): void {
    this.error.set(null);
  }

  crearItem(command: CentroLogisticoCementoUpsertCommand): Observable<void> {
    return this.execute(
      this.api.createItem(command).pipe(
        catchError((error) => {
          this.error.set('No se pudo crear el registro.');
          return throwError(() => error);
        }),
      ),
    );
  }

  editarItem(id: number, command: CentroLogisticoCementoUpsertCommand): Observable<void> {
    return this.execute(
      this.api.updateItem(id, command).pipe(
        catchError((error) => {
          this.error.set('No se pudo actualizar el registro.');
          return throwError(() => error);
        }),
      ),
    );
  }

  eliminarItem(id: number): Observable<void> {
    return this.execute(
      this.api.deleteItem(id).pipe(
        catchError((error) => {
          this.error.set('No se pudo desactivar el centro.');
          return throwError(() => error);
        }),
      ),
    );
  }

  subirExcelCargaMasiva(file: File): Observable<void> {
    return this.execute(this.api.postExcelCargaMasiva(file));
  }

  private execute(source$: Observable<void>): Observable<void> {
    this.beginLoading();
    return source$.pipe(finalize(() => this.endLoading()));
  }

  private loadItemsInternal(options: { propagateError: boolean }): Observable<void> {
    return this.execute(
      this.api
        .listItems({
          page: this.page(),
          pageSize: this.pageSize(),
          search: this.searchTerm().trim(),
          ordering: this.ordering(),
        })
        .pipe(
          map((response) => {
            this.totalItems.set(Math.max(0, Number(response.count) || 0));
            const effectiveLastPage = this.totalPages();
            if (this.page() > effectiveLastPage) {
              this.page.set(effectiveLastPage);
            }
            this.items.set(CentroLogisticoCementoMapper.toVmList(response.results));
            this.error.set(null);
          }),
          catchError((error) => {
            this.items.set([]);
            this.totalItems.set(0);
            this.error.set('No se pudieron cargar los centros logísticos.');
            if (options.propagateError) {
              return throwError(() => error);
            }
            return of(undefined);
          }),
        ),
    );
  }

  private beginLoading(): void {
    this.loadingDepth.update((depth) => depth + 1);
  }

  private endLoading(): void {
    this.loadingDepth.update((depth) => Math.max(0, depth - 1));
  }
}
